import React from "react";

import { HelmetProvider } from "react-helmet-async";
import { Provider as ReduxProvider } from "react-redux";
import { RouterProvider } from "react-router-dom";

import { createRoot } from "react-dom/client";

import { store } from "@mightybot/core";
import { I18nProvider } from "@mightybot/i18n";
import { Provider as BaseProvider } from "@mightybot/web-ui";

import AnalyticsIdentityBridge from "./components/AnalyticsIdentityBridge";
import { createAppRouter } from "./router";
import { initializeAnalytics } from "./utils/initializeAnalytics";
import { initializeLogger } from "./utils/initializeLogger";
import reportWebVitals from "./utils/reportWebVitals";

// Tailwind CSS and global styles
import "./styles/global.css";

// Add TypeScript declaration for module.hot at the top of the file
declare global {
	interface NodeModule {
		hot?: {
			accept(path?: string, callback?: () => void): void;
			decline(path?: string): void;
			dispose(callback: (data: any) => void): void;
			addDisposeHandler(callback: (data: any) => void): void;
			removeDisposeHandler(callback: (data: any) => void): void;
			status(): string;
		};
	}
}

// Initialize logger
initializeLogger();

// Create the router
const router = createAppRouter();

// Render the app
const container = document.getElementById("root");

// Create root once and reuse for HMR
const root = container ? createRoot(container) : null;

const render = () => {
	root?.render(
		<ReduxProvider store={store}>
			<I18nProvider
				baseUrl={(process.env.REACT_APP_WORKFLOW_API_URL ?? "").replace(
					/\/api\/?$/,
					"",
				)}
				getToken={() =>
					store.getState().authState?.accessToken ?? undefined
				}
			>
{/* REACT_APP_WORKFLOW_API_URL ends in /api; fetchCatalog appends
   /api/translations/catalog, so strip the trailing /api to avoid /api/api. */}
				<BaseProvider>
					<HelmetProvider>
						<AnalyticsIdentityBridge />
						<RouterProvider
							router={router}
							future={{
								v7_startTransition: true,
							}}
						/>
					</HelmetProvider>
				</BaseProvider>
			</I18nProvider>
		</ReduxProvider>,
	);
};

render();

// Defer analytics init until after first paint — keeps posthog-js off the critical path
if (typeof requestIdleCallback !== "undefined") {
	// timeout guarantees analytics still inits on a busy main thread (idle callback
	// can otherwise be delayed indefinitely).
	requestIdleCallback(
		() => {
			void initializeAnalytics();
		},
		{ timeout: 2000 },
	);
} else {
	setTimeout(() => {
		void initializeAnalytics();
	}, 0);
}

// Enable HMR with a more direct approach
if (module.hot) {
	// Accept the current module
	module.hot.accept();

	// Accept updates for the router
	module.hot.accept("./router", () => {
		console.log("HMR: Router update detected");
		render();
	});

	// Accept updates for any imported modules from other packages
	module.hot.accept("@mightybot/core", () => {
		console.log("HMR: Core package update detected");
		render();
	});

	module.hot.accept("@mightybot/web-ui", () => {
		console.log("HMR: Web UI package update detected");
		render();
	});

	module.hot.accept("@mightybot/core-ui", () => {
		console.log("HMR: Core UI package update detected");
		render();
	});
}

// Initialize service worker
// initializeServiceWorker();

// Report web vitals
reportWebVitals();
